import { MarkHearthArch as HearthMark } from "../components/brand/Marks";
import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { paymentStatus } from "../lib/api";
import { logError } from "../lib/log";
import { Button } from "../components/ui/button";
import { NoIndex } from "../components/Seo";
import { WelcomeBack } from "../components/pricing/WelcomeBack";

const MAX_TRIES = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (tries.current >= MAX_TRIES) {
        setState("timeout");
        return;
      }
      tries.current += 1;
      try {
        const res = await paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") {
          setState("error");
          return;
        }
      } catch (e) {
        logError("payment status poll failed, retrying", e);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      active = false;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background grid place-items-center px-6">
      <NoIndex title="Payment" />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-sm w-full text-center bg-card border border-border rounded-2xl p-8"
        data-testid="payment-success-page"
      >
        <div className="h-14 w-14 mx-auto rounded-2xl bg-primary text-primary-foreground grid place-items-center">
          <HearthMark className="h-7 w-7" />
        </div>
        {state === "polling" && (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mt-6" />
            <h1 className="mt-4 font-serif text-2xl">Confirming your upgrade…</h1>
            <p className="mt-1 text-sm text-muted-foreground">This only takes a moment.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mt-6" />
            <h1 className="mt-4 font-serif text-2xl" data-testid="payment-success-title">
              You're all set!
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your plan is active. Thank you for trusting HearthVault.
            </p>
            <div className="mt-4"><WelcomeBack compact /></div>
            <Button onClick={() => navigate("/vault")} className="mt-6 w-full bg-primary hover:bg-primary/90">
              Back to my vault
            </Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="h-10 w-10 text-amber-500 mx-auto mt-6" />
            <h1 className="mt-4 font-serif text-2xl">Still processing</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Your payment may take a moment to confirm. Your plan will update automatically.
            </p>
            <Button onClick={() => navigate("/vault")} variant="outline" className="mt-6 w-full">
              Back to my vault
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
